import { useMutation, useQuery } from '@apollo/client';
import React from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import thankIcon from '../../icons/non-prefilled/16x16/thanks.svg';
import getUserNames from '../../user/getUserNames';
import PostActionButton from './PostActionButton';
import styles from './PostThankButton.module.scss';
import { gql } from '../../graphql/generated/gql';

gql(`
  fragment PostThankButton_post on Post {
    id
    thanks
    thankers {
      id
      ...getUserNames_user
    }
  }
`);

const THANK_POST_MUTATION = gql(`
  mutation thankPost($id: ID!) {
    post: thankPost(id: $id) {
      ...PostThankButton_post
    }
  }
`);

const UNTHANK_POST_MUTATION = gql(`
  mutation unthankPost($id: ID!) {
    post: unthankPost(id: $id) {
      ...PostThankButton_post
    }
  }
`);

const THANK_BUTTON_QUERY = gql(`
  query thankButtonQuery($id: ID!) {
    post(id: $id) {
      ...PostThankButton_post
      publishedAt
    }
  }
`);

interface PostThankButtonProps {
  postId: string;
  hideCount?: boolean;
}

const PostThankButton = ({ postId, hideCount }: PostThankButtonProps) => {
  const currentUser = useAppSelector((state) => state.session.user);

  const { data } = useQuery(THANK_BUTTON_QUERY, { variables: { id: postId } });

  const [thankMutation] = useMutation(THANK_POST_MUTATION);

  const [unthankMutation] = useMutation(UNTHANK_POST_MUTATION);

  const post = data?.post;

  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/prefer-optional-chain
  if (!post || !post.publishedAt) {
    return null;
  }

  const { thankers, thanks } = post;

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const active =
    currentUser && thankers.some(({ id }) => currentUser.id === id);

  const toggleThank = () => {
    const mutation = active ? unthankMutation : thankMutation;

    mutation({
      variables: { id: postId },
      optimisticResponse: {
        __typename: 'RootMutationType',
        post: {
          __typename: 'Post',
          id: postId,
          thanks: !active ? thanks + 1 : thanks - 1,
          thankers: !active
            ? [...thankers, currentUser]
            : thankers.filter((u) => u.id !== currentUser.id),
        },
      },
    });
  };

  const tooltip = active ? 'Remove thank' : 'Thank authors';

  return (
    <div className="post-thank-button">
      <Tooltip
        placement="left"
        tooltip={tooltip}
        trigger="hover"
        popOverPortal={document.body}
      >
        <PostActionButton onClick={() => toggleThank()} aria-label={tooltip}>
          <SVGIcon className={active ? styles.thanked : undefined} size="large">
            {thankIcon}
          </SVGIcon>
        </PostActionButton>
      </Tooltip>
      {!hideCount && thankers.length > 0 && (
        <div className="thank-count">
          <Tooltip
            placement="left"
            tooltip={
              <div className="post-thank-tooltip">
                {getUserNames(thankers, { currentUser })}
              </div>
            }
            trigger="hover"
            popOverPortal={document.body}
          >
            {thankers.length}
          </Tooltip>
        </div>
      )}
    </div>
  );
};

export default PostThankButton;
